import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import LottieIcon from "../common/LottieIcon";
import FeatureTemplatePicker from "../../containers/Campaigns/FeatureTemplatePicker";
import EditablePreview from "../v2/CustomisationWorkspace/WorkspaceComponents/EditablePreview/EditablePreview";
import config from "../../config";
import { getIconCategoryClass } from "../common/iconCategory";

const normalizeId = (id) => (id === undefined || id === null ? null : String(id));

const toDisplayUrl = (url) =>
  url && url.startsWith('/files/') ? `${config.apiBaseUrl}${url}` : (url || '');

const LibraryPanel = ({ libraryItems, onDragStart, onDragEnd, onAddFromTemplate, onDeleteCampaignFeature, onSelectCampaignFeature, onHoverCampaignFeature, featuresList, selectedFeatureId, campaignData, customisationVars, leftListNewCustomisationItems }) => {
  const [librarySubTab, setLibrarySubTab] = useState('campaign');
  const [activeCategory, setActiveCategory] = useState('Features');
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [pendingDeleteItem, setPendingDeleteItem] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeletingItem, setIsDeletingItem] = useState(false);
  const categoriesRef = useRef(null);
  const [tooltipItem, setTooltipItem] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const tooltipTimerRef = useRef(null);

  const CATEGORY_ORDER = ['Features', 'Arcade/IRL', 'Effects', 'Core', 'Visual Components'];
  const allCategories = Object.keys(libraryItems).filter(c => c !== 'Campaign');
  const addNewCategories = [
    ...CATEGORY_ORDER.filter(c => allCategories.includes(c)),
    ...allCategories.filter(c => !CATEGORY_ORDER.includes(c) && c !== 'Misc'),
    ...(allCategories.includes('Misc') ? ['Misc'] : []),
  ];

  const campaignItems = libraryItems['Campaign'] || [];

  // Reset to first available category when items load
  useEffect(() => {
    if (addNewCategories.length > 0 && !addNewCategories.includes(activeCategory)) {
      setActiveCategory(addNewCategories[0]);
    }
  }, [addNewCategories.join(',')]);

  const updateScrollButtons = () => {
    if (categoriesRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = categoriesRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  useEffect(() => {
    updateScrollButtons();
    const categoriesEl = categoriesRef.current;
    if (categoriesEl) {
      categoriesEl.addEventListener('scroll', updateScrollButtons);
      window.addEventListener('resize', updateScrollButtons);
      return () => {
        categoriesEl.removeEventListener('scroll', updateScrollButtons);
        window.removeEventListener('resize', updateScrollButtons);
      };
    }
  }, [librarySubTab]);

  useEffect(() => {
    const container = categoriesRef.current;
    if (!container) return;
    const activeBtn = container.querySelector('.category-tab.active');
    if (!activeBtn) return;
    activeBtn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }, [activeCategory]);

  useEffect(() => {
    return () => clearTimeout(tooltipTimerRef.current);
  }, []);

  const scrollCategories = (direction) => {
    if (categoriesRef.current) {
      categoriesRef.current.scrollLeft += direction === 'right' ? 100 : -100;
    }
  };

  const closeDeleteModal = () => {
    if (isDeletingItem) {
      return;
    }

    setPendingDeleteItem(null);
    setDeleteConfirmText('');
  };

  const openDeleteModal = (item, event) => {
    event.preventDefault();
    event.stopPropagation();
    setPendingDeleteItem(item);
    setDeleteConfirmText('');
  };

  const handleDeleteSubmit = async (event) => {
    event.preventDefault();

    if (!pendingDeleteItem || !onDeleteCampaignFeature) {
      return;
    }

    setIsDeletingItem(true);
    const deleted = await onDeleteCampaignFeature(pendingDeleteItem);
    setIsDeletingItem(false);

    if (deleted) {
      setPendingDeleteItem(null);
      setDeleteConfirmText('');
    }
  };

  const handleItemClick = (item) => {
    if (item.isExistingCampaignFeature && onSelectCampaignFeature) {
      onSelectCampaignFeature(item);
    }
  };

  const handleItemHover = (item) => {
    if (item.isExistingCampaignFeature && onHoverCampaignFeature) {
      onHoverCampaignFeature(item);
    }
  };

  const handleItemHoverEnd = () => {
    onHoverCampaignFeature?.(null);
  };

  const handleCardHoverStart = (item, e) => {
    handleItemHover(item);
    if (item.isExistingCampaignFeature && campaignData) {
      const el = e.currentTarget;
      clearTimeout(tooltipTimerRef.current);
      tooltipTimerRef.current = setTimeout(() => {
        const rect = el.getBoundingClientRect();
        setTooltipPos({ top: rect.top, left: rect.right + 10 });
        setTooltipItem(item);
      }, 350);
    }
  };

  const handleCardHoverEnd = () => {
    handleItemHoverEnd();
    clearTimeout(tooltipTimerRef.current);
    setTooltipItem(null);
  };

  const renderItemCard = (item) => {
    const iconCatSlug = getIconCategoryClass(
      Array.isArray(item.iconCategory) ? item.iconCategory[0] : item.iconCategory || item.category || 'misc'
    );
    const showDeleteButton = item.isExistingCampaignFeature && onDeleteCampaignFeature;
    const isActive = Boolean(selectedFeatureId) && normalizeId(item.campaignFeatureId || item.id) === normalizeId(selectedFeatureId);
    if (item.fromTemplateName) {
      return (
        <div
          key={`${item.id}-${item.label}`}
          className={`ftp-card ${showDeleteButton ? 'library-deletable-card' : ''} ${isActive ? 'active' : ''}`}
          draggable
          onDragStart={(event) => onDragStart && onDragStart(item, event)}
          onDragEnd={(event) => onDragEnd && onDragEnd(event)}
          onClick={() => handleItemClick(item)}
          onMouseEnter={(e) => handleCardHoverStart(item, e)}
          onMouseLeave={() => handleCardHoverEnd()}
        >
          {showDeleteButton && (
            <button
              type="button"
              className="library-item-delete-btn"
              title={`Delete ${item.label}`}
              aria-label={`Delete ${item.label}`}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={(event) => openDeleteModal(item, event)}
              draggable={false}
            >
              <svg width="11" height="12" viewBox="-6 -6 12 12.5" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <rect x="-2.5" y="-6" width="5" height="2.5" rx="1.2" fill="#fee2e2" />
                <rect x="-6" y="-4" width="12" height="2" rx="1" fill="#fee2e2" />
                <rect x="-4.5" y="-1.5" width="9" height="8" rx="1" fill="#fee2e2" />
                <rect x="-2" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
                <rect x="0.8" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
              </svg>
            </button>
          )}
          <div className="ftp-card-img-wrap">
            <div
              className="ftp-card-img"
              style={item.fromTemplateImage ? { backgroundImage: `url(${toDisplayUrl(item.fromTemplateImage)})` } : {}}
            />
            <div className={`ftp-icon-badge category-icon-bg--${iconCatSlug}`}>
              <LottieIcon icon={item.icon} varKey={item.varKey} size={20} />
            </div>
          </div>
          <div className="ftp-card-label">{item.fromTemplateName}</div>
        </div>
      );
    }
    return (
      <div
        key={`${item.id}-${item.label}`}
        className={`library-item-card ${showDeleteButton ? 'library-deletable-card' : ''} ${isActive ? 'active' : ''}`}
        draggable
        onDragStart={(event) => onDragStart && onDragStart(item, event)}
        onDragEnd={(event) => onDragEnd && onDragEnd(event)}
        onClick={() => handleItemClick(item)}
        onMouseEnter={(e) => handleCardHoverStart(item, e)}
        onMouseLeave={() => handleCardHoverEnd()}
      >
        {showDeleteButton && (
          <button
            type="button"
            className="library-item-delete-btn"
            title={`Delete ${item.label}`}
            aria-label={`Delete ${item.label}`}
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onClick={(event) => openDeleteModal(item, event)}
            draggable={false}
          >
            <svg width="11" height="12" viewBox="-6 -6 12 12.5" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <rect x="-2.5" y="-6" width="5" height="2.5" rx="1.2" fill="#fee2e2" />
              <rect x="-6" y="-4" width="12" height="2" rx="1" fill="#fee2e2" />
              <rect x="-4.5" y="-1.5" width="9" height="8" rx="1" fill="#fee2e2" />
              <rect x="-2" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
              <rect x="0.8" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
            </svg>
          </button>
        )}
        <div className={`item-image-placeholder category-icon-bg--${iconCatSlug}`}>
          <LottieIcon icon={item.icon} varKey={item.varKey} size={48} />
        </div>
        <div className="item-name">{item.label}</div>
      </div>
    );
  };

  return (
    <>
    <div className="library-panel">
      {/* Sub-tabs */}
      <div className="library-sub-tabs">
        <button
          className={`library-sub-tab ${librarySubTab === 'campaign' ? 'active' : ''}`}
          onClick={() => setLibrarySubTab('campaign')}
        >
          Campaign
        </button>
        <button
          className={`library-sub-tab ${librarySubTab === 'add' ? 'active' : ''}`}
          onClick={() => { setLibrarySubTab('add'); setShowTemplatePicker(false); }}
        >
          Add New Feature
        </button>
      </div>

      {/* Campaign sub-tab */}
      {librarySubTab === 'campaign' && (
        <div className="library-items-grid">
          {campaignItems.length === 0 ? (
            <div className="library-empty"><p>No campaign features yet.</p></div>
          ) : (
            campaignItems.map(renderItemCard)
          )}
        </div>
      )}

      {/* Add New Feature sub-tab */}
      {librarySubTab === 'add' && (
        <>
          {showTemplatePicker ? (
            <FeatureTemplatePicker
              onSelect={(templateId) => {
                onAddFromTemplate && onAddFromTemplate(templateId, (success) => {
                  if (success) setShowTemplatePicker(false);
                });
              }}
              onBack={() => setShowTemplatePicker(false)}
              backLabel="← Back to Library"
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              featuresList={featuresList}
            />
          ) : (
            <>
              {addNewCategories.length === 0 ? (
                <div className="library-empty"><p>Loading features...</p></div>
              ) : (
                <>
                  <div className="library-categories-wrapper">
                    <button
                      className="category-scroll-btn"
                      onClick={() => scrollCategories('left')}
                      disabled={!canScrollLeft}
                      title="Scroll left"
                    >
                      ‹
                    </button>
                    <div className="library-categories" ref={categoriesRef}>
                      {addNewCategories.map((category) => (
                        <button
                          key={category}
                          className={`category-tab ${activeCategory === category ? 'active' : ''}`}
                          onClick={() => setActiveCategory(category)}
                        >
                          {category}
                        </button>
                      ))}
                    </div>
                    <button
                      className="category-scroll-btn"
                      onClick={() => scrollCategories('right')}
                      disabled={!canScrollRight}
                      title="Scroll right"
                    >
                      ›
                    </button>
                  </div>
                  <div className="library-items-grid">
                    {libraryItems[activeCategory]?.map(renderItemCard)}
                  </div>
                </>
              )}
              {onAddFromTemplate && (
                <div className="library-from-template-row">
                  <button className="library-from-template-btn" onClick={() => setShowTemplatePicker(true)}>
                    + Add Feature from Template
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {pendingDeleteItem && (
        <div className="flow-modal-overlay" onClick={closeDeleteModal}>
          <div className="flow-modal feature-delete-modal" onClick={(event) => event.stopPropagation()}>
            <h3>Delete Component?</h3>
            <p className="feature-delete-modal-copy">
              Are you sure you wish to delete <strong>{pendingDeleteItem.label}</strong>? This action cannot be undone.
            </p>
            <p className="feature-delete-modal-copy feature-delete-modal-copy--secondary">
              Please type <strong>confirm</strong> in the box to confirm this action.
            </p>
            <form onSubmit={handleDeleteSubmit}>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(event) => setDeleteConfirmText(event.target.value)}
                placeholder="Type confirm"
                autoFocus
              />
              <div className="flow-modal-buttons">
                <button type="button" onClick={closeDeleteModal}>
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isDeletingItem || deleteConfirmText.trim().toLowerCase() !== 'confirm'}
                  className="feature-delete-confirm-btn"
                >
                  {isDeletingItem ? 'Deleting...' : 'Delete Feature'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    {tooltipItem && ReactDOM.createPortal(
      (() => {
        const featureId = normalizeId(tooltipItem.campaignFeatureId || tooltipItem.id);
        const featureNode = leftListNewCustomisationItems?.[featureId] ?? null;
        const viewportH = typeof window !== 'undefined' ? window.innerHeight : 600;
        const tooltipH = 220;
        const clampedTop = Math.min(tooltipPos.top, viewportH - tooltipH - 16);
        return (
          <div className="library-preview-tooltip" style={{ top: clampedTop, left: tooltipPos.left }}>
            <div className="lpt-header">{tooltipItem.label}</div>
            <div className="lpt-body">
              {featureNode && campaignData ? (
                <EditablePreview
                  campaignData={campaignData}
                  customisationVars={customisationVars}
                  selectedFeatureId={featureId}
                  selectedFeatureNode={featureNode}
                  activeNode={featureNode}
                  focusEditingOnNode={() => {}}
                  setFocusEditingOnNode={() => {}}
                  visualisedNode={featureNode}
                  timelinePlayheadStep={0}
                  timelinePlayheadLabel={'Spinning'}
                  activateNodeFunction={() => {}}
                  forceUpdateComponent={() => {}}
                  forceUpdate={() => {}}
                  forceUpdateModuleListFunc={() => {}}
                  timelineHeight={0}
                  overridePreviewWindowId={`lptPreview_${featureId}`}
                  overrideContainerId={`lptContainer_${featureId}`}
                  thumbnailMode={true}
                />
              ) : (
                <div className="lpt-no-preview">No preview available</div>
              )}
            </div>
          </div>
        );
      })()
      , document.body
    )}
    </>
  );
};

export default LibraryPanel;
